import React, {
  forwardRef,
  useEffect,
  useMemo,
  useState,
  useImperativeHandle,
  useCallback,
  useRef,
} from "react";
import Box from "@mui/material/Box";
import DataTable from "../DataTable/DataTable";
import {
  gridFilteredSortedRowIdsSelector,
  useGridApiRef,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  GridToolbarDensitySelector,
} from "@mui/x-data-grid-pro";
import CommonButton from "../CommonButton/CommonButton";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import UndoIcon from "@mui/icons-material/Undo";
import { Tooltip, IconButton, Chip, CircularProgress } from "@mui/material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useDispatch } from "react-redux";
import { setRowCount } from "../../../features/portfolio/portfolioSlice";
import {
  fetchPortfolioData,
  deletePortfolioItem,
  updatePortfolioItem,
} from "../../../services/portfolioService";
import { useNavigate } from "react-router-dom";
import { Snackbar, Alert } from "@mui/material";
import PortfolioActionsModal from "../../../pages/Portfolio/modals/PortfolioActionsModal";
import { HistoryOutlined } from "@mui/icons-material";
import { useAuth } from "../../auth/AuthProvider";
import { useHistory } from "../../../context/HistoryContext";
import FlagIcon from "@mui/icons-material/Flag";
import { togglePortfolioFlag } from "../../../services/portfolioService";

/* =======================
    HELPER — safe money formatter
   ======================= */
const formatMoney = (value) => {
  if (value === null || value === undefined || value === "" || value === "—") return "—";
  if (isNaN(parseFloat(String(value).replace(/,/g, "")))) return value;
  const num = parseFloat(String(value).replace(/,/g, ""));
  return `${num.toFixed(2)}M`;
};
const formatDate = (value) => {
  if (!value || value === "—") return "—";
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    return date.toISOString().split("T")[0]; // "2017-12-01"
  } catch {
    return value;
  }
};

/* =======================
    EDITABLE FIELD MAP
   ======================= */
const EDITABLE_FIELD_LABELS = {
  rank: "Rank",
  grade: "Grade",
  kpi: "Top-line KPI",
  kpiValue: "Current KPI Value",
  news: "News",
  annualkpi: "Annual KPI Value",
  revenue: "Annual Revenue",
  cashBalance: "Net Cash Balance",
  monthlyBurn: "Monthly Burn",
  cashOutDate: "Cash Out Date",
};

const buildPayload = (newRow, oldRow) => {
  const payload = {};
  Object.keys(EDITABLE_FIELD_LABELS).forEach((f) => {
    if (newRow[f] !== oldRow[f]) payload[f] = newRow[f];
  });
  return payload;
};

const PortfolioTable = forwardRef(({ searchText, onSummaryChange, myPortfolioOnly }, ref) => {
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { openHistory } = useHistory();

  /* =======================
      STATE
     ======================= */
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [flagLoadingRows, setFlagLoadingRows] = useState({}); // { [rowId]: true }

  // dirtyRows: { [rowId]: { newRow, oldRow } }
  const [dirtyRows, setDirtyRows] = useState({});
  const dirtyCount = Object.keys(dirtyRows).length;

  /* =======================
      UNSAVED CHANGES WARNING
     ======================= */
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (dirtyCount > 0) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [dirtyCount]);

  /* =======================
      UTILITY FUNCTIONS
     ======================= */
  const getCompanyLogo = (companyName) => {
    const logoMap = {
      ServiceTitan: "https://cdn.brandfetch.io/servicetitan.com/fallback/lettermark/theme/dark/h/512/w/512/icon?c=1bfwsmEH20zzEfSNTed",
      Canva: "https://cdn.brandfetch.io/canva.com/fallback/lettermark/theme/dark/h/512/w/512/icon?c=1bfwsmEH20zzEfSNTed",
      Adobe: "https://cdn.brandfetch.io/adobe.com/fallback/lettermark/theme/dark/h/512/w/512/icon?c=1bfwsmEH20zzEfSNTed",
      Asana: "https://cdn.brandfetch.io/asana.com/fallback/lettermark/theme/dark/h/512/w/512/icon?c=1bfwsmEH20zzEfSNTed",
    };
    return (
      logoMap[companyName] ||
      `https://ui-avatars.com/api/?name=${encodeURIComponent(companyName)}&background=random&size=512`
    );
  };

  const transformApiData = (apiData) => {
    return apiData.map((item, index) => {
      const parseNum = (val) => {
        if (!val || val === "NA") return 0;
        const cleanVal = String(val).replace(/,/g, "");
        return parseFloat(cleanVal) || 0;
      };
      return {
        id: index + 1,
        apiId: item.rank,
        rank: item.rank || "—",
        company: item.Company || "—",
        logo: getCompanyLogo(item.Company || ""),
        reportingCurrency: item.Reporting_Currency || "USD" || "—",
        invCap: parseNum(item.Invested_Capital_in_Million_USD) || "—",
        invCapPercent: parseNum(item.Ownership_Percentage) || "—",
        grade: item.grade || "—",
        kpi: item.Top_line_KPI || "—",
        kpiValue: parseNum(item.Current_KPI_Value_in_Million),
        partner: item.partner_name || "—",
        partner_doc: item.Partner_Short_Name || "—",
        sector: item.company_sector || "—",
        location: item.Location || "—",
        news: item.News || "—",
        annualkpi: parseNum(item["Annual_KPI_Value_Forecast_in_Million"]) || "—",
        revenue: parseNum(item.Annual_Revenue_Forecast_in_Million) || "—",
        cashBalance: parseNum(item.Net_Cash_Balance_in_Million) || "—",
        monthlyBurn: item.Monthly_Burn_in_Million || "—",
        cashOutDate: item.Cash_Out_Date || "—",
        //flag: item.flag || "—",
        flag: item.flag === true || item.flag === "true",
        dealId: item.deal_id,
        arId: item.AR_Id,
      };
    });
  };

  /* =======================
      DATA FETCHING
     ======================= */
  const loadPortfolioData = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetchPortfolioData();
      if (response.status === "success" && response.data) {
        const transformedData = transformApiData(response.data);
        setAssets(transformedData);
        setDirtyRows({}); // clear any dirty state on fresh load
        const rowCount = transformedData.length;
        const amountInvested = transformedData.reduce((sum, row) => sum + (row.invCap || 0), 0);
        dispatch(setRowCount({ rowCount, amountInvested }));
      }
    } catch (error) {
      setError(error.message || "Failed to load portfolio data");
      showSnackbar("Failed to load portfolio data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPortfolioData();
  }, []);

  /* =======================
      INLINE EDITING — stage locally only, NO API call
     ======================= */
  const handleProcessRowUpdate = useCallback((newRow, oldRow) => {
    const changed = Object.keys(EDITABLE_FIELD_LABELS).some(
      (f) => newRow[f] !== oldRow[f]
    );

    if (!changed) return oldRow;

    // Stage the change — preserve original oldRow for potential discard
    setDirtyRows((prev) => ({
      ...prev,
      [newRow.id]: {
        newRow,
        // Keep the very first oldRow (before any edits) so discard always resets to original
        oldRow: prev[newRow.id]?.oldRow ?? oldRow,
      },
    }));

    return newRow; // grid reflects change visually immediately
  }, []);

  const handleProcessRowUpdateError = useCallback((error) => {
    showSnackbar("Error staging row update. Please try again.", "error");
    console.error("Row update error:", error);
  }, []);

  /* =======================
      SAVE ALL
     ======================= */
  const handleSaveAll = async () => {
    setSaving(true);
    const entries = Object.values(dirtyRows);

    const results = await Promise.allSettled(
      entries.map(({ newRow, oldRow }) => {
        const payload = buildPayload(newRow, oldRow);
        return updatePortfolioItem(newRow.arId, payload, user?.email, newRow.company);
      })
    );

    const failed = results
      .map((r, i) => (r.status === "rejected" ? entries[i] : null))
      .filter(Boolean);

    const succeeded = results
      .map((r, i) => (r.status === "fulfilled" ? entries[i] : null))
      .filter(Boolean);

    // Persist succeeded rows into assets
    if (succeeded.length > 0) {
      setAssets((prev) =>
        prev.map((row) => {
          const match = succeeded.find((e) => e.newRow.id === row.id);
          return match ? match.newRow : row;
        })
      );
    }

    // Keep only failed rows as still-dirty
    if (failed.length === 0) {
      setDirtyRows({});
      showSnackbar(`✓ ${succeeded.length} row(s) saved successfully`, "success");
    } else {
      const newDirty = {};
      failed.forEach(({ newRow, oldRow }) => {
        newDirty[newRow.id] = { newRow, oldRow };
      });
      setDirtyRows(newDirty);
      showSnackbar(
        `✗ ${failed.length} of ${entries.length} rows failed. Successful saves committed.`,
        "error"
      );
    }

    setSaving(false);
  };

  /* =======================
      DISCARD ALL
     ======================= */
  const handleDiscardAll = () => {
    // Revert grid rows back to their original values
    setAssets((prev) =>
      prev.map((row) => {
        const dirty = dirtyRows[row.id];
        return dirty ? dirty.oldRow : row;
      })
    );
    setDirtyRows({});
    showSnackbar("All unsaved changes discarded", "info");
  };

  /* =======================
      PER-ROW DISCARD
     ======================= */
  const handleDiscardRow = useCallback((rowId) => {
    setAssets((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row;
        return dirtyRows[rowId]?.oldRow ?? row;
      })
    );
    setDirtyRows((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  }, [dirtyRows]);

  /* =======================
      PER-ROW SAVE
     ======================= */
  const handleSaveRow = useCallback(async (rowId) => {
    const entry = dirtyRows[rowId];
    if (!entry) return;
    const { newRow, oldRow } = entry;
    const payload = buildPayload(newRow, oldRow);
    try {
      await updatePortfolioItem(newRow.arId, payload, user?.email, newRow.company);
      setAssets((prev) => prev.map((r) => (r.id === rowId ? newRow : r)));
      setDirtyRows((prev) => { const n = { ...prev }; delete n[rowId]; return n; });
      showSnackbar(`✓ ${newRow.company} saved`, "success");
    } catch {
      showSnackbar(`✗ Failed to save ${newRow.company}`, "error");
    }
  }, [dirtyRows, user?.email]);

  /* =======================
      OTHER HANDLERS
     ======================= */
  const handleCloseModal = () => { setModalOpen(false); setSelectedRow(null); };
  const handleEdit = (rowData) => navigate(`/company/${rowData.id}/edit`);
  const handleDelete = async (rowData) => {
    try {
      await deletePortfolioItem(rowData.apiId || rowData.id);
      setAssets((prev) => prev.filter((item) => item.id !== rowData.id));
      showSnackbar(`${rowData.company} deleted successfully`, "success");
    } catch (error) {
      showSnackbar(`Failed to delete ${rowData.company}`, "error");
    }
  };
  const handleToggleFlag = useCallback(async (row) => {
    const currentFlag = row.flag === true || row.flag === "true";
    const newFlagValue = !currentFlag;

    setFlagLoadingRows((prev) => ({ ...prev, [row.id]: true })); // ← start loading

    try {
      await togglePortfolioFlag(row.arId, newFlagValue, user?.email, row.company);
      setAssets((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, flag: newFlagValue } : r))
      );
      showSnackbar(
        `${row.company}: flag ${newFlagValue ? "raised 🚩" : "cleared"}`,
        "success"
      );
    } catch {
      showSnackbar(`Failed to update flag for ${row.company}`, "error");
    } finally {
      setFlagLoadingRows((prev) => { // ← always stop loading
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
    }
  }, []);

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });
  const handleCloseSnackbar = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));

  /* =======================
      FILTERED ASSETS
     ======================= */
    const filteredAssets = useMemo(() => {
    let baseData = [...assets];
    if (myPortfolioOnly) {
      const activeName = "rms";
      baseData = baseData.filter((row) => {
        const rowPartner = (row.partner || "").toLowerCase().trim();
        const rowDoc = (row.partner_doc || "").toLowerCase().trim();
        return rowPartner === activeName || rowDoc === activeName;
      });
    }
    if (searchText && searchText.trim() !== "") {
      const query = searchText.toLowerCase().trim();
      const isNumeric = !isNaN(query) && query !== "";

      baseData = baseData.filter((row) => {
        // Text columns — partial match is fine
        const textMatch = [row.company, row.partner, row.sector, row.location]
          .some((field) => String(field || "").toLowerCase().includes(query));

        // Numeric columns — exact match only
        const numericMatch = isNumeric && [row.rank]
          .some((field) => String(field || "") === query);

        return textMatch || numericMatch;
      });
    }
    return baseData;
  }, [assets, searchText, myPortfolioOnly, user]);

  useEffect(() => {
    const rowCount = filteredAssets.length;
    const amountInvested = filteredAssets.reduce((sum, row) => sum + (row.invCap || 0), 0);
    onSummaryChange?.({ rowCount, amountInvested });
  }, [filteredAssets]);

  // Sync footer when DataGrid's internal filter panel changes
  useEffect(() => {
    if (!apiRef.current) return;

    const unsubscribe = apiRef.current.subscribeEvent("filteredRowsSet", () => {
      const visibleRowIds = gridFilteredSortedRowIdsSelector(apiRef.current.state);
      const visibleRows = visibleRowIds.map((id) => apiRef.current.getRow(id));
      const rowCount = visibleRows.length;
      const amountInvested = visibleRows.reduce((sum, row) => sum + (row?.invCap || 0), 0);
      onSummaryChange?.({ rowCount, amountInvested });
    });

    return () => unsubscribe();
  }, [apiRef, onSummaryChange]);

  /* =======================
      IMPERATIVE HANDLE
     ======================= */
  useImperativeHandle(ref, () => ({
    openColumnsPanel: () => apiRef.current?.showPreferences("columns"),
    openFiltersPanel: () => apiRef.current?.showPreferences("filters"),
    exportToExcel() {
      if (!apiRef.current) return;
      const state = apiRef.current.state;
      const visibleRowIds = gridFilteredSortedRowIdsSelector(state);
      if (visibleRowIds.length === 0) { showSnackbar("No data matches the current filters", "warning"); return; }
      const visibilityModel = state.columns.columnVisibilityModel || {};
      const visibleColumns = apiRef.current.getAllColumns().filter((col) =>
        visibilityModel[col.field] !== false && col.field !== "actions"
      );
      const exportData = visibleRowIds.map((id) => {
        const row = apiRef.current.getRow(id);
        const rowData = {};
        visibleColumns.forEach((col) => {
          const header = col.headerName || col.field;
          if (col.field === "invCap") {
            const cap = typeof row.invCap === "number" ? row.invCap.toFixed(2) : row.invCap;
            const pct = typeof row.invCapPercent === "number" ? row.invCapPercent.toFixed(2) : row.invCapPercent;
            rowData[header] = `$${cap}M (${pct}%)`;
          } else if (typeof row[col.field] === "number") {
            rowData[header] = row[col.field].toFixed(2);
          } else {
            rowData[header] = row[col.field];
          }
        });
        return rowData;
      });
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Portfolio");
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      saveAs(blob, `Portfolio_${new Date().toLocaleDateString()}.xlsx`);
    },
    refreshData: loadPortfolioData,
  }));

  /* =======================
      CUSTOM TOOLBAR
     ======================= */
  const CustomToolbar = () => (
    <GridToolbarContainer sx={{ p: 1, borderBottom: "1px solid #e0e0e0" }}>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
      {myPortfolioOnly && (
        <Chip
          icon={<EditIcon />}
          label="Editing Mode Active"
          color="primary"
          size="small"
          sx={{ ml: "auto", mr: 1 }}
        />
      )}
    </GridToolbarContainer>
  );

  /* =======================
      COLUMNS DEFINITION
     ======================= */
  const columns = useMemo(() => [
    {
      field: "company",
      headerName: "Company",
      width: 220,
      minWidth: 180,
      filterable: true,
      sortable: true,
      editable: false,
      renderCell: (params) => (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            if (dirtyRows[params.row.id]) return; // block nav if row is dirty
            navigate(`/company/${encodeURIComponent(params.row.company)}`, {
              state: {
                kpi: params.row.kpi,
                partner_doc: params.row.partner_doc,
                deal_id: params.row.dealId,
                ar_id: params.row.arId,
              },
            });
          }}

        >
          <img
            src={params.row.logo}
            alt={params.value}
            style={{ width: 24, height: 24, borderRadius: "50%" }}
            onError={(e) => {
              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(params.value)}&background=random`;
            }}
          />
          {params.value}
        </Box>
      ),
    },
    {
      field: "partner",
      headerName: "Partners",
      width: 130,
      filterable: true,
      sortable: true,
      editable: false,
    },
    /* {
      field: "rank",
      headerName: "Rank",
      width: 70,
      align: "right",
      headerAlign: "right",
      filterable: true,
      sortable: true,
      type: "number",
      editable: myPortfolioOnly,
      renderHeader: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <span>{params.colDef.headerName}</span>
          {myPortfolioOnly && <EditIcon sx={{ fontSize: 12, color: "#1976d2", ml: 0.5 }} />}
        </Box>
      ),
    }, */
    {
      field: "rank",
      headerName: "Rank",
      width: 70,
      align: "right",
      headerAlign: "right",
      filterable: true,
      sortable: true,
      type: "number",
      editable: myPortfolioOnly,

      // Clamp to positive on commit
      valueParser: (value) => {
        const num = Number(value);
        if (isNaN(num) || num < 1) return 1;
        return Math.floor(num); // integers only
      },

      // Block negative input at the cell level
      renderEditCell: (params) => (
        <input
          type="number"
          min="1"
          step="1"
          defaultValue={params.value}
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            outline: "none",
            textAlign: "right",
            fontSize: "0.875rem",
            padding: "0 8px",
            backgroundColor: "#fff3e0",
          }}
          onKeyDown={(e) => {
            // Block minus sign and 'e' (scientific notation)
            if (e.key === "-" || e.key === "e" || e.key === "E") {
              e.preventDefault();
            }
          }}
          onChange={(e) => {
            let val = parseInt(e.target.value, 10);
            if (isNaN(val) || val < 1) val = 1;
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: val,
            });
          }}
        />
      ),

      renderHeader: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <span>{params.colDef.headerName}</span>
          {myPortfolioOnly && <EditIcon sx={{ fontSize: 12, color: "#1976d2", ml: 0.5 }} />}
        </Box>
      ),
    },
    {
      field: "grade",
      headerName: "Grade",
      width: 90,
      filterable: true,
      sortable: true,
      editable: myPortfolioOnly,
      type: "singleSelect",
      valueOptions: ["A", "A-", "A+", "B", "B-", "B+", "C", "C-", "C+", "D", "D-", "F", "NA"],
      renderHeader: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <span>{params.colDef.headerName}</span>
          {myPortfolioOnly && <EditIcon sx={{ fontSize: 12, color: "#1976d2", ml: 0.5 }} />}
          <Tooltip title="Company performance grade based on KPIs, revenue growth, and financial health. A = Excellent, B = Good, C = Average, D = Needs Improvement" arrow placement="top">
            <IconButton size="small" sx={{ p: 0.25 }}>
              <HelpOutlineIcon sx={{ fontSize: 14, color: "#9CA3AF" }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "invCap",
      headerName: "Inv. Cap (% Own)",
      flex: 1,
      minWidth: 150,
      align: "right",
      headerAlign: "right",
      filterable: true,
      sortable: true,
      type: "number",
      editable: false,
      renderCell: (params) => {
        const cap = parseFloat(params.value);
        const pct = parseFloat(params.row.invCapPercent);
        return <span>${isNaN(cap) ? "—" : cap.toFixed(2)}M ({isNaN(pct) ? "—" : pct.toFixed(2)}%)</span>;
      },
    },
    {
      field: "sector",
      headerName: "Sector",
      width: 140,
      filterable: true,
      sortable: true,
      editable: false,
    },
    {
      field: "location",
      headerName: "Location",
      width: 140,
      filterable: true,
      sortable: true,
      editable: false,
    },
    {
      field: "kpi",
      headerName: "Top-line KPI",
      flex: 1,
      minWidth: 120,
      filterable: true,
      sortable: true,
      editable: myPortfolioOnly,
      type: "singleSelect",
      valueOptions: ["ARR", "CARR", "Quarterly Revenue", "None", "Monthly Revenue", "AUM", "Deployment Revenue", "Rev. Run Rate", "Quarterly EBITDA", "Monthly GMV", "YTD Bookings"],
      renderHeader: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <span>{params.colDef.headerName}</span>
          {myPortfolioOnly && <EditIcon sx={{ fontSize: 12, color: "#1976d2" }} />}
        </Box>
      ),
    },
    {
      field: "reportingCurrency",
      headerName: "Reporting Currency",
      width: 130,
      filterable: true,
      sortable: true,
      editable: false,
    },
    {
      field: "kpiValue",
      headerName: "Cur. KPI Value",
      flex: 1,
      minWidth: 130,
      align: "right",
      headerAlign: "right",
      filterable: true,
      sortable: true,
      type: "number",
      editable: myPortfolioOnly,
      renderHeader: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <span>{params.colDef.headerName}</span>
          {myPortfolioOnly && <EditIcon sx={{ fontSize: 12, color: "#1976d2" }} />}
        </Box>
      ),
      renderCell: (params) => formatMoney(params.value),
      valueFormatter: (value) => value,
      valueParser: (value) => Number(value),
    },
    {
      field: "annualkpi",
      headerName: "An. KPI Value",
      flex: 1,
      minWidth: 120,
      align: "right",
      headerAlign: "right",
      filterable: true,
      sortable: true,
      type: "number",
      editable: myPortfolioOnly,
      renderHeader: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <span>{params.colDef.headerName}</span>
          {myPortfolioOnly && <EditIcon sx={{ fontSize: 12, color: "#1976d2" }} />}
        </Box>
      ),
      renderCell: (params) => formatMoney(params.value),
      valueFormatter: (value) => value,
      valueParser: (value) => Number(value),
    },
    {
      field: "revenue",
      headerName: "An. Revenue",
      flex: 1,
      minWidth: 120,
      align: "right",
      headerAlign: "right",
      filterable: true,
      sortable: true,
      type: "number",
      editable: myPortfolioOnly,
      renderHeader: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <span>{params.colDef.headerName}</span>
          {myPortfolioOnly && <EditIcon sx={{ fontSize: 12, color: "#1976d2" }} />}
        </Box>
      ),
      renderCell: (params) => formatMoney(params.value),
      valueFormatter: (value) => value,
      valueParser: (value) => Number(value),
    },
    {
      field: "cashBalance",
      headerName: "Net Cash Balance",
      width: 160,
      align: "right",
      headerAlign: "right",
      filterable: true,
      sortable: true,
      type: "number",
      editable: myPortfolioOnly,
      renderHeader: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <span>{params.colDef.headerName}</span>
          {myPortfolioOnly && <EditIcon sx={{ fontSize: 12, color: "#1976d2" }} />}
        </Box>
      ),
      renderCell: (params) => formatMoney(params.value),
      valueFormatter: (value) => value,
      valueParser: (value) => Number(value),
    },
    {
      field: "monthlyBurn",
      headerName: "Monthly Burn",
      width: 140,
      align: "right",
      headerAlign: "right",
      filterable: true,
      sortable: true,
      type: "number",
      editable: myPortfolioOnly,
      renderHeader: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <span>{params.colDef.headerName}</span>
          {myPortfolioOnly && <EditIcon sx={{ fontSize: 12, color: "#1976d2" }} />}
        </Box>
      ),
      renderCell: (params) => formatMoney(params.value),
      valueFormatter: (value) => value || 0,
      valueParser: (value) => (value ? Number(value) : 0),
    },
    /* {
      field: "cashOutDate",
      headerName: "Cash Out Date",
      width: 140,
      filterable: true,
      sortable: true,
      editable: myPortfolioOnly,
      renderHeader: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <span>{params.colDef.headerName}</span>
          {myPortfolioOnly && <EditIcon sx={{ fontSize: 12, color: "#1976d2" }} />}
        </Box>
      ),
    }, */
    {
      field: "cashOutDate",
      headerName: "Cash Out Date",
      width: 140,
      filterable: true,
      sortable: true,
      editable: myPortfolioOnly,

      // Display: always show as yyyy-MM-dd (strip time)
      renderCell: (params) => {
        const val = params.value;
        if (!val || val === "—") return <span>—</span>;
        try {
          const date = new Date(val);
          if (isNaN(date.getTime())) return <span>{val}</span>;
          // Force yyyy-MM-dd regardless of locale
          const yyyy = date.getUTCFullYear();
          const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
          const dd = String(date.getUTCDate()).padStart(2, "0");
          return <span>{`${yyyy}-${mm}-${dd}`}</span>;
        } catch {
          return <span>{val}</span>;
        }
      },

      // Edit: date input pre-filled as yyyy-MM-dd (what <input type="date"> requires)
      renderEditCell: (params) => {
        const toInputValue = (val) => {
          if (!val || val === "—") return "";
          try {
            const date = new Date(val);
            if (isNaN(date.getTime())) return "";
            const yyyy = date.getUTCFullYear();
            const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
            const dd = String(date.getUTCDate()).padStart(2, "0");
            return `${yyyy}-${mm}-${dd}`; // "2017-12-01" — required by input[type=date]
          } catch {
            return "";
          }
        };

        return (
          <input
            type="date"
            defaultValue={toInputValue(params.value)}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              outline: "none",
              fontSize: "0.875rem",
              padding: "0 8px",
              backgroundColor: "#fff3e0",
              cursor: "pointer",
            }}
            onChange={(e) => {
              const raw = e.target.value; // "2017-12-01" or "" if cleared
              // Send ISO string back, or "" which service layer converts to null
              const isoValue = raw ? `${raw}T00:00:00.000Z` : "";
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: isoValue,
              });
            }}
          />
        );
      },

      renderHeader: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <span>{params.colDef.headerName}</span>
          {myPortfolioOnly && <EditIcon sx={{ fontSize: 12, color: "#1976d2" }} />}
        </Box>
      ),
    },
    {
      field: "news",
      headerName: "News",
      flex: 1,
      minWidth: 200,
      filterable: true,
      sortable: true,
      editable: myPortfolioOnly,
      renderHeader: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <span>{params.colDef.headerName}</span>
          {myPortfolioOnly && <EditIcon sx={{ fontSize: 12, color: "#1976d2" }} />}
        </Box>
      ),
    },
    /*  {
       field: "flag",
       headerName: "Flag",
       width: 100,
       filterable: true,
       sortable: true,
       editable: false,
       renderHeader: (params) => (
         <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
           <span>{params.colDef.headerName}</span>
           <Tooltip title="Alert status: Red = Critical, Yellow = Warning, Green = Healthy, — = No flag" arrow placement="top">
             <IconButton size="small" sx={{ p: 0.25 }}>
               <HelpOutlineIcon sx={{ fontSize: 14, color: "#9CA3AF" }} />
             </IconButton>
           </Tooltip>
         </Box>
       ),
     }, */
    {
      field: "flag",
      headerName: "Flag",
      width: 80,
      filterable: true,
      sortable: true,
      editable: false,
      renderHeader: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <span>{params.colDef.headerName}</span>
          <Tooltip title="Flag for Century Team attention" arrow placement="top">
            <IconButton size="small" sx={{ p: 0.25 }}>
              <HelpOutlineIcon sx={{ fontSize: 14, color: "#9CA3AF" }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      renderCell: (params) => {
        const isFlagged = params.value === true;
        const isLoading = !!flagLoadingRows[params.row.id];

        // myPortfolioOnly — clickable with spinner
        if (myPortfolioOnly) {
          return (
            <Tooltip title={isFlagged ? "Remove flag" : "Flag for Century Team"} arrow>
              <IconButton
                size="small"
                disabled={isLoading}
                onClick={(e) => { e.stopPropagation(); handleToggleFlag(params.row); }}
                sx={{
                  color: isFlagged ? "#1976d2" : "#9CA3AF",
                  transition: "color 0.2s",
                  "&:hover": { color: isFlagged ? "#1565c0" : "#6B7280" },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={16} sx={{ color: isFlagged ? "#1976d2" : "#9CA3AF" }} />
                ) : (
                  <FlagIcon
                    fontSize="small"
                    sx={{
                      transition: "transform 0.2s ease",
                      transform: isFlagged ? "scale(1.15)" : "scale(1)",
                    }}
                  />
                )}
              </IconButton>
            </Tooltip>
          );
        }

        // Read-only mode — always show flag icon, blue if flagged, gray if not
        return (
          <Tooltip title={isFlagged ? "Flagged for Century Team" : "Not flagged"} arrow>
            <FlagIcon
              fontSize="small"
              sx={{ color: isFlagged ? "#1976d2" : "#9CA3AF" }}
            />
          </Tooltip>
        );
      },
    },
    // ── Actions — swaps to Save/Discard when row is dirty
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      minWidth: 140,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: (params) => {
        const isDirty = !!dirtyRows[params.row.id];
        return (
          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", height: "100%" }}>
            {isDirty ? (
              <>
                <Tooltip title="Save this row">
                  <CommonButton
                    variant="contained"
                    size="small"
                    color="success"
                    sx={{ height: "30px", minWidth: "32px", width: "32px", borderRadius: "50%", p: 0 }}
                    onClick={(e) => { e.stopPropagation(); handleSaveRow(params.row.id); }}
                  >
                    <SaveIcon fontSize="small" />
                  </CommonButton>
                </Tooltip>
                <Tooltip title="Discard changes">
                  <CommonButton
                    variant="outlined"
                    size="small"
                    color="error"
                    sx={{ height: "30px", minWidth: "32px", width: "32px", borderRadius: "50%", p: 0 }}
                    onClick={(e) => { e.stopPropagation(); handleDiscardRow(params.row.id); }}
                  >
                    <UndoIcon fontSize="small" />
                  </CommonButton>
                </Tooltip>
              </>
            ) : (
              <>
                <CommonButton
                  variant="contained"
                  size="small"
                  color="secondary"
                  sx={{ height: "30px", textTransform: "none" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/company/${encodeURIComponent(params.row.company)}/overview`, {
                      state: {
                        kpi: params.row.kpi,
                        partner_doc: params.row.partner_doc,
                        partner_name: params.row.partner,
                        deal_id: params.row.dealId,
                        ar_id: params.row.arId,
                        companyName: params.row.company,
                      },
                    });
                  }}
                >
                  View
                </CommonButton>
                <CommonButton
                  variant="contained"
                  color="secondary"
                  sx={{ minWidth: "32px", width: "32px", height: "30px", borderRadius: "50%", p: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    openHistory(params.row.company, params.row.id);
                  }}
                >
                  <HistoryOutlined fontSize="small" />
                </CommonButton>
              </>
            )}
          </Box>
        );
      },
    },
  ], [myPortfolioOnly, navigate, dirtyRows, handleSaveRow, handleDiscardRow, handleToggleFlag, flagLoadingRows]);

  /* =======================
      RENDER
     ======================= */
  return (
    <Box sx={{ height: "65vh", width: "100%", display: "flex", flexDirection: "column" }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <DataTable
        apiRef={apiRef}
        rows={filteredAssets}
        columns={columns}
        loading={loading}
        pagination={true}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        editMode="row"
        processRowUpdate={handleProcessRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        slots={{ toolbar: CustomToolbar }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
        initialState={{
          pagination: { pageSize: 25, page: 0 },
          pinnedColumns: { left: ["company"], right: ["actions"] },
          filter: { filterModel: { items: [] } },
        }}
        filterMode="client"
        sortingMode="client"
        // Highlight dirty rows with a yellow-left-border class
        getRowClassName={(params) => dirtyRows[params.row.id] ? "row--dirty" : ""}
        /* onRowClick={(params, event) => {
          if (myPortfolioOnly && event.target.closest(".MuiDataGrid-cell--editable")) return;
          if (dirtyRows[params.row.id]) return; 
          navigate(`/company/${encodeURIComponent(params.row.company)}`, {
            state: { kpi: params.row.kpi, partner_doc: params.row.partner_doc, deal_id: params.row.dealId },
          });
        }} */
        sx={{
          flex: 1,
          cursor: myPortfolioOnly ? "default" : "pointer",
          // Dirty row highlight
          "& .row--dirty": {
            borderLeft: "3px solid #f59e0b",
            backgroundColor: "#fffbeb !important",
            "&:hover": { backgroundColor: "#fef3c7 !important" },
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#f5f5f5 !important",
            borderBottom: "2px solid #e0e0e0",
          },
          "& .MuiDataGrid-cell": {
            borderRight: "1px solid #e0e0e0",
            borderBottom: "1px solid #f0f0f0",
          },
          "& .MuiDataGrid-cell--editable": {
            backgroundColor: myPortfolioOnly ? "#f0f7ff" : "transparent",
            cursor: myPortfolioOnly ? "pointer" : "default",
            "&:hover": { backgroundColor: myPortfolioOnly ? "#e3f2fd" : "transparent" },
          },
          "& .MuiDataGrid-cell--editing": {
            backgroundColor: "#fff3e0",
            boxShadow: "inset 0 0 0 2px #1976d2",
          },
          "& .MuiDataGrid-toolbarContainer": {
            padding: "8px",
            gap: "8px",
            borderBottom: "1px solid #e0e0e0",
            backgroundColor: "#fafafa",
          },
          "& .MuiButton-root": { textTransform: "none", fontSize: "0.875rem" },
          "& .MuiDataGrid-cell--pinnedLeft": {
            backgroundColor: "#fff !important",
            borderRight: "2px solid #d0d0d0 !important",
            boxShadow: "2px 0 4px rgba(0,0,0,0.05)",
          },
          "& .MuiDataGrid-columnHeader--pinnedLeft": {
            backgroundColor: "#f5f5f5 !important",
            borderRight: "2px solid #d0d0d0 !important",
            boxShadow: "2px 0 4px rgba(0,0,0,0.05)",
          },
          "& .MuiDataGrid-cell--pinnedRight": {
            backgroundColor: "#fff !important",
            borderLeft: "2px solid #d0d0d0 !important",
            boxShadow: "-2px 0 4px rgba(0,0,0,0.05)",
          },
          "& .MuiDataGrid-columnHeader--pinnedRight": {
            backgroundColor: "#f5f5f5 !important",
            borderLeft: "2px solid #d0d0d0 !important",
            boxShadow: "-2px 0 4px rgba(0,0,0,0.05)",
          },
          "& ::-webkit-scrollbar": { height: "7px", width: "7px" },
          "& ::-webkit-scrollbar-thumb": {
            background: "#d0d0d0",
            borderRadius: "3px",
            "&:hover": { background: "#b0b0b0" },
          },
        }}
      />

      {/* ── Sticky Save All Banner — only visible when there are dirty rows */}
      {myPortfolioOnly && dirtyCount > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.25,
            bgcolor: "#1e40af",
            color: "#fff",
            borderRadius: "0 0 8px 8px",
            boxShadow: "0 -2px 8px rgba(0,0,0,0.15)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EditIcon fontSize="small" />
            <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>
              {dirtyCount} unsaved row{dirtyCount > 1 ? "s" : ""} — changes are staged locally
            </span>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <CommonButton
              variant="outlined"
              size="small"
              disabled={saving}
              sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.5)", "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.1)" } }}
              onClick={handleDiscardAll}
            >
              Discard All
            </CommonButton>
            <CommonButton
              variant="contained"
              size="small"
              disabled={saving}
              sx={{ bgcolor: "#fff", color: "#1e40af", "&:hover": { bgcolor: "#e0e7ff" } }}
              onClick={handleSaveAll}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon fontSize="small" />}
            >
              {saving ? "Saving…" : `Save All (${dirtyCount})`}
            </CommonButton>
          </Box>
        </Box>
      )}

      <PortfolioActionsModal
        open={modalOpen}
        onClose={handleCloseModal}
        rowData={selectedRow}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default PortfolioTable;